import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Wine, Utensils, TreePine, Sparkles, Music, MapPin, Phone, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const ChateauBelleville = () => {
  const navigate = useNavigate();
  const [bookingData, setBookingData] = useState({
    checkIn: '',
    checkOut: '',
    guests: '2'
  });

  const handleBooking = (e) => {
    e.preventDefault();
    alert('This is a demo site. Booking functionality is not active.');
  };

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 bg-amber-800 text-white p-3 rounded-full shadow-lg hover:bg-amber-900 transition-all duration-300 hover:scale-110"
      >
        <ArrowLeft className="h-6 w-6" />
      </button>

      {/* Hero */}
      <header className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1445019980597-93fa8acb246c?w=1920&q=80"
            alt="Chateau"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-amber-50"></div>
        </div>

        <div className="relative h-full flex flex-col">
          <nav className="container mx-auto px-6 py-8 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Crown className="h-10 w-10 text-amber-400" />
              <div>
                <span className="text-2xl font-serif text-white">Château Belleville</span>
                <p className="text-amber-300 text-xs tracking-widest">DEPUIS 1742</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-8 text-white/90 text-sm font-light">
              <button onClick={() => document.getElementById('heritage').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-300">Heritage</button>
              <button onClick={() => document.getElementById('suites').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-300">Suites</button>
              <button onClick={() => document.getElementById('experiences').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-300">Experiences</button>
              <button onClick={() => document.getElementById('booking').scrollIntoView({ behavior: 'smooth' })} className="hover:text-amber-300">Reservations</button>
            </div>
          </nav>

          <div className="flex-1 flex items-center justify-center text-center px-6">
            <div className="max-w-4xl">
              <p className="text-amber-300 tracking-[0.3em] uppercase text-sm mb-8 font-light">A Heritage of Excellence</p>
              <h1 className="text-6xl md:text-8xl font-serif text-white mb-8 leading-tight">
                Where History<br />
                <span className="italic">Embraces</span> Luxury
              </h1>
              <p className="text-xl text-white/80 max-w-2xl mx-auto font-light">
                An 18th-century château transformed into an intimate luxury hotel, 
                nestled in the heart of the Swiss wine country.
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Heritage Section */}
      <section id="heritage" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-amber-700 tracking-widest uppercase text-sm mb-4 font-light">Our Story</p>
              <h2 className="text-5xl font-serif text-amber-900 mb-6">A Living Legacy</h2>
              <p className="text-amber-800/70 leading-relaxed mb-6 font-light">
                Built in 1742 by the Belleville family, our château has witnessed nearly three centuries of history. 
                Today, we preserve this heritage while offering modern luxury, creating an experience that honors 
                the past while embracing the present.
              </p>
              <p className="text-amber-800/70 leading-relaxed font-light">
                Each room tells a story, each corridor whispers tales of grand balls and distinguished guests. 
                We invite you to become part of our continuing narrative.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&q=80" alt="Chateau exterior" className="w-full h-48 object-cover" />
              <img src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&q=80" alt="Interior" className="w-full h-48 object-cover mt-8" />
              <img src="https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=400&q=80" alt="Gardens" className="w-full h-48 object-cover -mt-8" />
              <img src="https://images.unsplash.com/photo-1590490360182-c33d57733427?w=400&q=80" alt="Room" className="w-full h-48 object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Suites Section */}
      <section id="suites" className="py-24 bg-amber-900 text-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-amber-300 tracking-widest uppercase text-sm mb-4 font-light">Accommodations</p>
            <h2 className="text-5xl font-serif">Royal Suites</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "La Duchesse Suite", price: "CHF 750", image: "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&q=80", desc: "Original 18th-century frescoes, four-poster bed" },
              { name: "Le Marquis Room", price: "CHF 580", image: "https://images.unsplash.com/photo-1591088398332-8a7791972843?w=600&q=80", desc: "Vineyard views, antique furnishings" },
              { name: "La Tour Suite", price: "CHF 920", image: "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80", desc: "Private tower, 360° views, fireplace" }
            ].map((room, i) => (
              <div key={i} className="group">
                <div className="relative h-72 overflow-hidden mb-6">
                  <img src={room.image} alt={room.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <h3 className="text-2xl font-serif mb-2">{room.name}</h3>
                <p className="text-amber-200/70 font-light mb-4">{room.desc}</p>
                <p className="text-amber-300">From {room.price}<span className="text-amber-200/50 text-sm"> / night</span></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experiences Section */}
      <section id="experiences" className="py-24 bg-amber-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-amber-700 tracking-widest uppercase text-sm mb-4 font-light">Curated Moments</p>
            <h2 className="text-5xl font-serif text-amber-900">Château Experiences</h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { icon: Wine, name: "Wine Cellar Tour", desc: "Private tasting of our historic collection" },
              { icon: Utensils, name: "Gastronomic Dining", desc: "Michelin-starred French cuisine" },
              { icon: TreePine, name: "Garden Walks", desc: "Guided tours of our 18th-century gardens" },
              { icon: Music, name: "Chamber Concerts", desc: "Intimate classical performances" }
            ].map((exp, i) => (
              <div key={i} className="text-center p-8 bg-white border border-amber-200 hover:border-amber-400 transition-colors">
                <exp.icon className="h-10 w-10 text-amber-700 mx-auto mb-4" />
                <h3 className="text-lg font-serif text-amber-900 mb-2">{exp.name}</h3>
                <p className="text-amber-700/60 text-sm font-light">{exp.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 bg-amber-800 text-white">
        <div className="container mx-auto px-6 max-w-3xl">
          <div className="text-center mb-12">
            <p className="text-amber-300 tracking-widest uppercase text-sm mb-4 font-light">Reservations</p>
            <h2 className="text-5xl font-serif">Reserve Your Stay</h2>
          </div>

          <form onSubmit={handleBooking} className="bg-amber-900/50 p-10 border border-amber-600/30">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div>
                <Label className="text-amber-200 mb-2 block text-sm font-light">Arrival</Label>
                <Input type="date" value={bookingData.checkIn} onChange={(e) => setBookingData({...bookingData, checkIn: e.target.value})} className="bg-amber-800/50 border-amber-600/50 text-white h-12" required />
              </div>
              <div>
                <Label className="text-amber-200 mb-2 block text-sm font-light">Departure</Label>
                <Input type="date" value={bookingData.checkOut} onChange={(e) => setBookingData({...bookingData, checkOut: e.target.value})} className="bg-amber-800/50 border-amber-600/50 text-white h-12" required />
              </div>
              <div>
                <Label className="text-amber-200 mb-2 block text-sm font-light">Guests</Label>
                <select value={bookingData.guests} onChange={(e) => setBookingData({...bookingData, guests: e.target.value})} className="w-full h-12 px-3 bg-amber-800/50 border border-amber-600/50 text-white rounded-md">
                  {[1,2,3,4].map(n => <option key={n} value={n}>{n} Guest{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
            </div>
            <Button type="submit" className="w-full bg-amber-400 hover:bg-amber-300 text-amber-900 py-6 text-lg font-serif">
              Request Availability
            </Button>
            <p className="text-center text-amber-300/50 text-xs mt-4 italic font-light">* Demo site - booking not functional</p>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-amber-950">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center text-amber-300/70 mb-8">
            <div><MapPin className="h-6 w-6 mx-auto mb-2" /><p className="font-light">Lavaux, Switzerland</p></div>
            <div><Phone className="h-6 w-6 mx-auto mb-2" /><p className="font-light">+41 21 799 0000</p></div>
            <div><Mail className="h-6 w-6 mx-auto mb-2" /><p className="font-light">concierge@chateaubelleville.ch</p></div>
          </div>
          <p className="text-center text-amber-400/50 text-sm font-light">© 2026 Château Belleville • Demo Site</p>
        </div>
      </footer>
    </div>
  );
};

export default ChateauBelleville;
